// Rogers payment_spec contract over Watts Official fields (not Excel MCO Calcs).
// Serve Official EM/ER/TM when present; app owns harvest-leg stress + farmer protection.
// Data: MarginCoverageOption/Official (Watts) — not Databricks/MSSQL mirror.

import { num, toShareDecimal } from "./mcoShared.js";

/** Half-up to `digits` decimals (Rogers round2). */
export function roundHalfUp(v, digits = 2) {
  const p = 10 ** digits;
  const x = Number(v);
  if (!Number.isFinite(x)) return NaN;
  const signed = x < 0 ? -1 : 1;
  return (signed * Math.round(Math.abs(x) * p + Number.EPSILON)) / p;
}

const round2 = (v) => roundHalfUp(v, 2);

/**
 * Cap harvest crop price at 2× projected (crop price only — never inputs).
 * @param {number} harvestPrice
 * @param {number} projectedPrice
 */
export function capHarvestPrice(harvestPrice, projectedPrice) {
  const hp = num(harvestPrice);
  const pp = num(projectedPrice);
  if (!(hp > 0) || !(pp > 0)) return hp;
  return Math.min(hp, 2 * pp);
}

/**
 * Trigger margin (TM).
 * Base: round2(EM − ER × (1 − TL)).
 * Plan 68 only, when HP > PP: recompute with harvest price refresh.
 * Prefer Official TM when present and no 68 recompute applies.
 *
 * @param {object} p
 */
export function computeTriggerMargin(p) {
  const em = num(p.expectedMargin);
  const er = num(p.expectedRevenue);
  const tl = num(p.triggerLevel ?? 0.95);
  const eay = num(p.expectedYield);
  const pp = num(p.projectedPrice);
  const hp = capHarvestPrice(p.harvestPrice, pp);
  const planCode = String(p.planCode || "68");
  const officialTM =
    p.officialTriggerMargin == null || p.officialTriggerMargin === "" ? null : num(p.officialTriggerMargin);

  const needs68Refresh = planCode === "68" && hp > pp && eay > 0;
  if (needs68Refresh) {
    const maxP = Math.max(pp, hp);
    const costBasket = er - em;
    return round2(eay * maxP - costBasket - eay * maxP * (1 - tl));
  }
  if (officialTM != null && Number.isFinite(officialTM)) return officialTM;
  return round2(em - er * (1 - tl));
}

/**
 * Rogers Layer-A projected payment from Official + stress legs.
 *
 * @param {object} input
 * @param {number} input.expectedYield — EAY (Official expectedIndexValue)
 * @param {number} input.finalYield — stress / final area yield
 * @param {number} input.projectedPrice
 * @param {number} input.harvestPrice
 * @param {number} input.expectedRevenue — Official ER (never recompute)
 * @param {number} input.expectedMargin — Official EM (signed OK)
 * @param {number} [input.officialTriggerMargin] — serve when no 68 refresh
 * @param {number} [input.projectedCosts] — for harvest-cost hold
 * @param {number|null} [input.harvestCosts] — null → hold at projected (27-MCO)
 * @param {number} input.approvedYield — farmer APH
 * @param {string|number} [input.planCode=68]
 * @param {number} [input.coverageStart=0.95] — trigger level TL
 * @param {number} [input.areaLossEnd=0.86]
 * @param {number} [input.coverageRange] — else TL − end
 * @param {number} [input.protectionFactor=1] — coverage % 0.50–1.00
 * @param {number} [input.sharePct=100]
 * @param {number} [input.acres=1]
 */
export function computeMcoProjectedPayment(input) {
  const expectedYield = num(input.expectedYield);
  const finalYield = num(input.finalYield);
  const projectedPrice = num(input.projectedPrice);
  const harvestPriceRaw = num(input.harvestPrice);
  const expectedRevenue = num(input.expectedRevenue);
  const expectedMargin = num(input.expectedMargin);
  const approvedYield = num(input.approvedYield);
  const triggerLevel = num(input.coverageStart ?? 0.95);
  const areaLossEnd = num(input.areaLossEnd ?? 0.86);
  const coverageRange = num(input.coverageRange ?? round2(triggerLevel - areaLossEnd));
  const planCode = String(input.planCode || "68");
  const protectionFactor = num(input.protectionFactor ?? 1);
  const share = toShareDecimal(input.sharePct);
  const projectedCosts = num(input.projectedCosts);

  let harvestCosts = input.harvestCosts == null || input.harvestCosts === "" ? null : num(input.harvestCosts);
  const harvestCostsHeldAtExpected = harvestCosts == null && Number.isFinite(projectedCosts);
  if (harvestCostsHeldAtExpected) harvestCosts = projectedCosts;

  const blockers = [];
  // Official NULL → no-data (never invent $0 guarantee / PF=0).
  if (input.expectedRevenue == null || input.expectedRevenue === "" || Number.isNaN(expectedRevenue)) {
    blockers.push("expected revenue missing");
  }
  if (input.expectedMargin == null || input.expectedMargin === "" || Number.isNaN(expectedMargin)) {
    blockers.push("expected margin missing");
  }
  if (!(expectedYield > 0)) blockers.push("expected yield missing");
  if (!(projectedPrice > 0)) blockers.push("projected crop price missing");
  if (!(harvestPriceRaw > 0)) blockers.push("harvest crop price missing");
  if (!(approvedYield > 0)) blockers.push("approved yield (APH) missing");
  if (!(coverageRange > 0)) blockers.push("invalid coverage band");
  if (!(triggerLevel > areaLossEnd)) blockers.push("invalid coverage band");
  if (harvestCosts == null || Number.isNaN(harvestCosts)) {
    blockers.push("harvest costs N/A — cannot compute projected payment");
  }
  if (!(finalYield >= 0) || Number.isNaN(finalYield)) {
    blockers.push("final yield missing");
  }
  if (!(protectionFactor > 0)) blockers.push("protection factor missing");

  if (blockers.length) {
    return { ready: false, blockers, result: null };
  }

  const harvestPrice = capHarvestPrice(harvestPriceRaw, projectedPrice);
  const triggerMargin = computeTriggerMargin({
    expectedMargin,
    expectedRevenue,
    triggerLevel,
    expectedYield,
    projectedPrice,
    harvestPrice,
    planCode,
    officialTriggerMargin: input.officialTriggerMargin,
  });

  // Harvest revenue: YP → FY×PP; RP / RP-HPE → FY×HP (capped).
  const harvestRevPrice = planCode === "67" ? projectedPrice : harvestPrice;
  const harvestRevenue = round2(finalYield * harvestRevPrice);
  const harvestMargin = round2(harvestRevenue - harvestCosts);

  const coverageValue = expectedRevenue * coverageRange; // full precision
  if (!(coverageValue > 0)) {
    return {
      ready: false,
      blockers: ["coverage value unavailable — no data"],
      result: null,
    };
  }

  // PF unrounded in calc; clamp [0, 1.000].
  let paymentFactor = (triggerMargin - harvestMargin) / coverageValue;
  paymentFactor = Math.min(1, Math.max(0, paymentFactor));

  // Farmer protection $/ac (RP ECV uses max(PP,HP)).
  const ecvPrice = planCode === "68" ? Math.max(projectedPrice, harvestPrice) : projectedPrice;
  const protectionPerAcre = round2(approvedYield * ecvPrice * coverageRange * protectionFactor * share);
  const mcoPayment = round2(paymentFactor * protectionPerAcre);

  return {
    ready: true,
    blockers: [],
    result: {
      expectedRevenue: round2(expectedRevenue),
      expectedMargin: round2(expectedMargin),
      expectedCosts: round2(expectedRevenue - expectedMargin),
      coverageRange,
      coverageValue: round2(coverageValue),
      triggerMargin,
      harvestRevenue,
      harvestMargin,
      harvestPrice,
      harvestPriceCapped: harvestPrice !== harvestPriceRaw,
      harvestCosts,
      harvestCostsHeldAtExpected,
      deficient: round2(triggerMargin - harvestMargin),
      paymentFactor,
      protectionPerAcre,
      mcoPayment,
      planCode,
      label: "projected",
      source: "watts-official",
    },
  };
}
